import React, { useState, useMemo } from 'react'
import { string, func } from 'prop-types'
import { Button } from '@livechat/design-system-react-components'
import { css } from '@emotion/react'

import { PRODUCT_PAGE_TABS } from '@/constants'
import { useClientLoadedContext } from '@/context/clientLoaded'
import { MarkdownRender } from '@/components/MarkdownRender'
import Section from '@/components/Product/Section'
import ChangelogModal from './ChangelogModal'

const linkButton = css`
  font-size: 16px;
`

const getLastVersionLog = (changelog) => {
  if (!changelog?.length) {
    return null
  }

  const LAST_VERSION_REGEX = new RegExp('<h[1-6]>(.*?)<hr\\s*\\/>', 's')
  const matches = LAST_VERSION_REGEX.exec(changelog)
  const lastVersion = matches?.[0]?.replace('<hr />', '')

  return lastVersion || changelog
}

const Changelog = ({ changelog, innerRef }) => {
  const isClientLoaded = useClientLoadedContext()
  const [isChangelogModalOpen, setChangelogModalOpen] = useState(false)
  const toggleModal = () => setChangelogModalOpen((prev) => !prev)
  const lastUpdate = useMemo(() => getLastVersionLog(changelog), [changelog])

  // Disabled on the client side because LAST_VERSION_REGEX wouldn't work for the client side markdown renderer
  if (!changelog || !lastUpdate || isClientLoaded) {
    return null
  }

  return (
    <Section
      id={PRODUCT_PAGE_TABS.CHANGELOG}
      title="What's new"
      action={
        <Button
          css={linkButton}
          kind="link-light"
          onClick={toggleModal}
          data-test="changelog-button"
        >
          Version history
        </Button>
      }
      ref={innerRef}
    >
      <MarkdownRender content={lastUpdate} isRawMarkdown={isClientLoaded} />

      {isChangelogModalOpen && (
        <ChangelogModal changelog={changelog} onClose={toggleModal} />
      )}
    </Section>
  )
}

Changelog.propTypes = {
  changelog: string,
  innerRef: func,
}

export default React.forwardRef((props, ref) => (
  <Changelog innerRef={ref} {...props} />
))
